import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { setToken } from '../lib/auth-store.js';

const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? PATH_PREFIX_NORMALIZED;

export function LoginPage(): JSX.Element {
  const [mode, setMode] = useState<'password' | 'token'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setTokenLocal] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const onPasswordSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Email and password required');
      return;
    }
    if (totpRequired && !/^\d{6}$/.test(totpCode)) {
      setError('Enter the 6-digit code from your authenticator');
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, string> = { email: email.trim(), password };
      if (totpRequired) body.totp_code = totpCode;
      const res = await fetch(BASE_URL + '/v1/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const respBody = await res.json().catch(() => ({}));
        const type = typeof respBody === 'object' && respBody && 'type' in respBody ? String(respBody.type) : '';
        if (type === 'totp_required') {
          setTotpRequired(true);
          setError('Two-factor code required');
          return;
        }
        if (type === 'invalid_totp') {
          setError('That code did not match. Try the next one.');
          return;
        }
        if (res.status === 429) setError('Too many attempts. Wait a minute and try again.');
        else if (res.status === 401) setError('Invalid email or password');
        else if (res.status === 403) setError('Account is disabled');
        else setError(typeof respBody === 'object' && respBody && 'title' in respBody ? String(respBody.title) : `Login failed (${res.status})`);
        return;
      }
      setToken(null);
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const onTokenSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setError(null);
    const trimmed = token.trim();
    if (!trimmed) {
      setError('Token cannot be empty');
      return;
    }
    if (!/^[\w-]+\.[\w-]+\.[\w-]+$/.test(trimmed)) {
      setError("Doesn't look like a JWT (header.payload.signature)");
      return;
    }
    setToken(trimmed);
    void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    navigate('/');
  };

  const onGoogle = (): void => {
    // Fire-and-redirect — the server responds with 302 to Google.
    window.location.href = BASE_URL + '/api/auth/oauth/google/start?intent=login';
  };

  return (
    <div className="mx-auto max-w-md">
      <h1 className="text-2xl font-semibold mb-2">Sign in</h1>

      <div className="flex gap-2 mb-6 text-xs">
        <button
          type="button"
          onClick={() => setMode('password')}
          className={`px-2 py-1 rounded ${mode === 'password' ? 'bg-accent text-accent-fg' : 'bg-surface-alt text-ink-muted hover:text-ink'}`}
        >
          Email + password
        </button>
        <button
          type="button"
          onClick={() => setMode('token')}
          className={`px-2 py-1 rounded ${mode === 'token' ? 'bg-accent text-accent-fg' : 'bg-surface-alt text-ink-muted hover:text-ink'}`}
        >
          Paste OAuth token
        </button>
      </div>

      {mode === 'password' ? (
        <>
          <button
            type="button"
            onClick={onGoogle}
            className="w-full mb-3 flex items-center justify-center gap-2 rounded border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-surface-alt"
          >
            <GoogleG />
            Continue with Google
          </button>
          <div className="relative my-4">
            <hr className="border-border" />
            <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-surface px-2 text-xs text-ink-muted">
              or
            </span>
          </div>
          <form onSubmit={onPasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); setTotpRequired(false); }}
                className="w-full rounded border border-border bg-surface text-ink p-2 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); setTotpRequired(false); }}
                className="w-full rounded border border-border bg-surface text-ink p-2 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            {totpRequired && (
              <div>
                <label htmlFor="totp" className="block text-sm font-medium mb-1">
                  Two-factor code
                </label>
                <input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => { setTotpCode(e.target.value.replace(/\D/g, '')); setError(null); }}
                  className="w-full rounded border border-border bg-surface text-ink p-2 font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-accent"
                  autoFocus
                />
                <p className="text-xs text-ink-muted mt-1">
                  6-digit code from your authenticator app.
                </p>
              </div>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {submitting ? 'Signing in…' : totpRequired ? 'Verify and sign in' : 'Sign in'}
            </button>
            <div className="flex justify-between text-xs">
              <Link to="/forgot-password" className="text-accent hover:underline">
                Forgot password?
              </Link>
            </div>
          </form>
        </>
      ) : (
        <form onSubmit={onTokenSubmit} className="space-y-4">
          <p className="text-ink-muted text-sm">
            Paste a JWT from <code className="rounded bg-surface-alt px-1">POST /v1/oauth/token</code>.
          </p>
          <textarea
            value={token}
            onChange={(e) => { setTokenLocal(e.target.value); setError(null); }}
            rows={5}
            placeholder="eyJhbGciOi..."
            className="w-full rounded border border-border bg-surface text-ink p-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {error && <p className="text-sm text-danger">{error}</p>}
          <button
            type="submit"
            className="rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium hover:opacity-90"
          >
            Use token
          </button>
        </form>
      )}
    </div>
  );
}

function GoogleG(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.7A5.41 5.41 0 0 1 3.65 9c0-.59.1-1.16.3-1.7V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.99-2.34z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"/>
    </svg>
  );
}
